import "../styles/globals.css";
import type { AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import { useRouter } from "next/router";
import * as log from 'loglevel';
import { RainbowKitProvider, connectorsForWallets } from '@rainbow-me/rainbowkit';
import {
  binanceWallet,
  metaMaskWallet,
  walletConnectWallet,
  injectedWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WagmiProvider, createConfig, http, useAccount, useSwitchChain } from 'wagmi';
import { bsc } from 'viem/chains';
import { useEffect } from 'react';
import '@rainbow-me/rainbowkit/styles.css';
import { PlaysoutAuthProvider } from '../components/PlaysoutAuthProvider';

// 全局日志级别设置 - 生产环境默认静默，开发环境默认 info
log.setLevel((process.env.NEXT_PUBLIC_LOG_LEVEL || (process.env.NODE_ENV === 'production' ? 'silent' : 'info')) as log.LogLevelDesc);

// 根据环境变量决定使用的链 - 移到组件外部，避免重新创建
const defaultChain = bsc;
const supportedChains = [bsc] as const;

// WalletConnect 开关 - 默认禁用
const enableWalletConnect = process.env.NEXT_PUBLIC_ENABLE_WALLETCONNECT === 'true';

// 自定义钱包连接器 - 移到组件外部，避免重新创建导致连接状态丢失
const wallets = [
  binanceWallet,
  injectedWallet,      // 支持币安钱包、Trust Wallet 等内置浏览器
  metaMaskWallet,      // MetaMask
  ...(enableWalletConnect ? [walletConnectWallet] : []), // WalletConnect（需配置 projectId）
];

const connectors = connectorsForWallets(
  [
    {
      groupName: 'Recommended Wallets',
      wallets,
    },
  ],
  {
    appName: 'Playsout Mini Games',
    projectId: process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID || 'placeholder',
  }
);

// 创建 Wagmi 配置 - 移到组件外部，确保配置实例唯一
const config = createConfig({
  connectors,
  chains: supportedChains,
  transports: {
    [bsc.id]: http()
  },
  ssr: true, // 启用 SSR 支持，确保服务端和客户端状态一致
});

// 创建查询客户端 - 移到组件外部，避免缓存丢失
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // 防止窗口聚焦时重新获取数据
      retry: false,
    },
  },
});

// 添加一个内部组件来处理自动切换网络（简化实现）
function AutoSwitchToBSC() {
  const { isConnected, chain } = useAccount();
  const { switchChain } = useSwitchChain();

  useEffect(() => {
    if (isConnected && chain?.id !== bsc.id) {
      try {
        switchChain({ chainId: bsc.id });
        console.log('已切换到 BSC');
      } catch (err) {
        console.error('切换失败:', err);
      }
    }
  }, [isConnected, chain, switchChain]);

  return null; // 无UI，仅逻辑
}

function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      // @ts-ignore
      window.gtag?.('config', 'G-V5RLQX9XRV', { page_path: url });
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      <Head>
        <link
          rel="preload"
          href="/fonts/AdelleSans-Regular.woff"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/AdelleSans-Regular.woff2"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/AdelleSans-Semibold.woff"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/AdelleSans-Semibold.woff2"
          as="font"
          crossOrigin=""
        />

        <link rel="icon" href="/favicons/favicon.ico" sizes="any" />
        <link rel="icon" href="/favicons/icon.svg" type="image/svg+xml" />
        <link rel="apple-touch-icon" href="/favicons/apple-touch-icon.png" />
        <link rel="manifest" href="/favicons/manifest.json" />

        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no" />
        <title>Playsout Mini Games</title>
        <meta name="description" content="Playsout Mini Games" />
      </Head>

      {/* Google Analytics 4 */}
      <Script src="https://www.googletagmanager.com/gtag/js?id=G-V5RLQX9XRV" strategy="afterInteractive" />
      <Script id="gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-V5RLQX9XRV', { page_path: window.location.pathname });
        `}
      </Script>

      <WagmiProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <RainbowKitProvider
            initialChain={defaultChain}
            locale="en-US"
          >
            <AutoSwitchToBSC />
            <PlaysoutAuthProvider>
              <Component {...pageProps} />
            </PlaysoutAuthProvider>
          </RainbowKitProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </>
  );
}

export default MyApp;
